import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import Button from "@mui/material/Button";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { toast } from "react-toastify";
import { TRANSFER_FUNDS } from "../../../graphql/account";
import transferFunding from "../../../validations/transferFunding";
import { BankAccount } from "hooks/useGetBankAccounts";
import { Stack } from "components/layout/reusableLayouts";
import {
  AccountSelect,
  AmountField,
  ConfirmTransfer,
  MemoField,
  TransferAccountsRow,
} from "./TransferFormFields";

interface FormikTransferFundsDrawerProps {
  fromAccount: string;
  toAccount: string;
  amount: string;
  memo: string;
  confirmTransfer: boolean;
}

interface TransferFundsDrawerProps {
  open: boolean;
  handleCloseDrawer: (isReload: boolean) => void;
  fromAccountsData: BankAccount[];
  toAccountsData: BankAccount[];
  refetchAccounts: () => void;
}

dayjs.extend(utc);

const TransferFundsDrawer: React.FC<TransferFundsDrawerProps> = (props) => {
  const {
    open,
    handleCloseDrawer,
    fromAccountsData,
    toAccountsData,
    refetchAccounts,
  } = props;

  const { t } = useTranslation();

  const [transferFunds, { loading: loadingTransfer }] = useMutation(
    TRANSFER_FUNDS,
    {
      onCompleted: () => {
        toast.success(t("Transfer created!"));
        handleCloseDrawer(true);
        formik.resetForm();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  const formik = useFormik<FormikTransferFundsDrawerProps>({
    initialValues: {
      fromAccount: "",
      toAccount: "",
      amount: "",
      memo: "",
      confirmTransfer: false,
    },
    validationSchema: transferFunding,
    onSubmit: async (values) => {
      const { fromAccount, toAccount, amount, memo } = values;

      await transferFunds({
        variables: {
          input: {
            fromAccountId: fromAccount,
            toAccountId: toAccount,
            amount: Number(amount),
            memo: memo,
          },
        },
      });
    },
  });

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (toAccountsData?.length > 0 && formik.values.toAccount === "") {
      formik.setFieldValue("toAccount", toAccountsData[0].id);
    }
  }, [toAccountsData, formik]);

  useEffect(() => {
    if (fromAccountsData?.length > 0 && formik.values.fromAccount === "") {
      formik.setFieldValue("fromAccount", fromAccountsData[0].id);
    }
  }, [fromAccountsData, formik]);

  const handleClose = () => {
    if (loadingTransfer) return;
    formik.resetForm();
    handleCloseDrawer(false);
  };

  const errorFor = (field: keyof FormikTransferFundsDrawerProps) =>
    formik.touched[field] ? (formik.errors[field] as string) : null;

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Transfer Funds")}
      width="544px"
      loading={loadingTransfer}
      footer={
        <>
          <Button variant="primary-outlined" onClick={handleClose}>
            {t("Cancel")}
          </Button>

          <Button
            variant="primary-filled"
            // No `isValid` gate: `handleSubmit` validates, touches every field
            // so the errors paint, and refuses to submit an invalid form.
            disabled={!formik.values.confirmTransfer || loadingTransfer}
            onClick={() => formik.handleSubmit()}
          >
            {loadingTransfer ? t("Transferring") : t("Complete")}
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="var(--space-6)">
          <TransferAccountsRow
            from={
              <AccountSelect
                name="fromAccount"
                ariaLabel={t("From")}
                value={formik.values.fromAccount}
                accounts={fromAccountsData}
                onSelect={(id) => formik.setFieldValue("fromAccount", id)}
                errorText={errorFor("fromAccount")}
              />
            }
            to={
              <AccountSelect
                name="toAccount"
                ariaLabel={t("To")}
                value={formik.values.toAccount}
                accounts={toAccountsData}
                onSelect={(id) => formik.setFieldValue("toAccount", id)}
                errorText={errorFor("toAccount")}
                // The old copy wrote the new account id to a `fundingAccount`
                // field this form does not have, so linking a bank here never
                // selected it.
                onAccountAdded={(id) => formik.setFieldValue("toAccount", id)}
                refetchAccounts={refetchAccounts}
              />
            }
          />

          <AmountField
            value={formik.values.amount}
            onChange={(raw) => formik.setFieldValue("amount", raw)}
            helperText={t("External transfers take 3-5 business days")}
            errorText={errorFor("amount")}
          />

          <MemoField
            value={formik.values.memo}
            onChange={(memo) => formik.setFieldValue("memo", memo)}
          />

          <ConfirmTransfer
            checked={formik.values.confirmTransfer}
            onChange={(checked) =>
              formik.setFieldValue("confirmTransfer", checked)
            }
            errorText={errorFor("confirmTransfer")}
            label={t("AchDebitAuthorization")}
          />
        </Stack>
      </form>
    </Drawer>
  );
};

export default TransferFundsDrawer;
